import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const CalCalculator = () => {
  const [tipo, setTipo] = useState('calcitica');
  const [teorCaO, setTeorCaO] = useState(92);
  const [teorMgO, setTeorMgO] = useState(40);
  const [precoProduto, setPrecoProduto] = useState(520);
  const [frete, setFrete] = useState(80);
  const [incluirFrete, setIncluirFrete] = useState(true);
  const [results, setResults] = useState({
    precoTotal: 0,
    custoPorPonto: 0,
    custoPorKgOxido: 0,
    interpretacao: ''
  });

  const calcular = useCallback(() => {
    const teor = tipo === 'calcitica' ? teorCaO : teorMgO;
    const precoTotal = precoProduto + (incluirFrete ? frete : 0);
    const custoPorPonto = teor > 0 ? (precoTotal / teor) : 0;
    const kgOxidoPorTon = (teor / 100) * 1000;
    const custoPorKgOxido = kgOxidoPorTon > 0 ? (precoTotal / kgOxidoPorTon) : 0;

    const linha1 = `Preço considerado: ${precoTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} por tonelada.`;
    const linha2 = `Você está pagando ${custoPorPonto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} por ponto percentual de ${tipo === 'calcitica' ? 'CaO' : 'MgO'} e ${custoPorKgOxido.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} por kg de ${tipo === 'calcitica' ? 'CaO' : 'MgO'} efetivo.`;
    const linha3 = (teor >= 90 && tipo === 'calcitica') ? 'Excelente pureza para cal calcítica.'
                 : (teor >= 35 && tipo === 'dolomitica') ? 'Bom teor de MgO para cal dolomítica.'
                 : 'Avalie laudos e regularidade do fornecedor para garantir performance.';

    setResults({
      precoTotal,
      custoPorPonto,
      custoPorKgOxido,
      interpretacao: `${linha1} ${linha2} ${linha3}`
    });
  }, [tipo, teorCaO, teorMgO, precoProduto, frete, incluirFrete]);

  useEffect(() => {
    calcular();
  }, [calcular]);

  const teorAtual = tipo === 'calcitica' ? teorCaO : teorMgO;

  const qualidade = useMemo(() => {
    const base = tipo === 'calcitica' ? teorCaO : teorMgO;

    if (tipo === 'calcitica') {
      if (base >= 95) return { label: 'Qualidade premium', tone: 'bg-primary/10 text-primary border-primary/20' };
      if (base >= 90) return { label: 'Muito boa pureza', tone: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' };
      if (base >= 85) return { label: 'Pureza média', tone: 'bg-amber-500/10 text-amber-600 border-amber-500/20' };
      return { label: 'Verificar especificações', tone: 'bg-destructive/10 text-destructive border-destructive/20' };
    }

    if (base >= 38) return { label: 'Excelente teor de MgO', tone: 'bg-primary/10 text-primary border-primary/20' };
    if (base >= 34) return { label: 'Bons níveis de MgO', tone: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' };
    if (base >= 30) return { label: 'Dentro da média', tone: 'bg-amber-500/10 text-amber-600 border-amber-500/20' };
    return { label: 'Requer atenção aos laudos', tone: 'bg-destructive/10 text-destructive border-destructive/20' };
  }, [tipo, teorCaO, teorMgO]);

  return (
    <Card className="border-0 bg-background shadow-none">
      <CardHeader className="space-y-6 pb-8 text-center">
        <CardTitle className="text-xl md:text-2xl font-medium tracking-tight text-foreground">
          Calculadora de Eficiência da Cal
        </CardTitle>
        <p className="text-sm text-muted-foreground max-w-xl mx-auto leading-loose">
          Compare o custo real por óxido ativo para facilitar sua decisão de compra.
        </p>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="grid gap-16 lg:grid-cols-[1.1fr,0.9fr]">
          <div className="space-y-10">
            <div className="space-y-4">
              <Label className="text-sm font-medium text-foreground">Tipo de cal</Label>
              <div className="flex flex-wrap gap-2">
                <label
                  className={`flex w-full sm:w-auto items-center justify-between gap-3 rounded-lg border px-4 py-2.5 text-sm transition ${
                    tipo === 'calcitica'
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border hover:border-primary/30'
                  }`}
                >
                  <span className="font-normal">Calcítica (CaO)</span>
                  <input
                    type="radio"
                    name="tipo"
                    value="calcitica"
                    checked={tipo === 'calcitica'}
                    onChange={(e) => setTipo(e.target.value)}
                    className="h-4 w-4 text-primary"
                    aria-label="Selecionar cal calcítica"
                  />
                </label>
                <label
                  className={`flex w-full sm:w-auto items-center justify-between gap-3 rounded-lg border px-4 py-2.5 text-sm transition ${
                    tipo === 'dolomitica'
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border hover:border-primary/30'
                  }`}
                >
                  <span className="font-normal">Dolomítica (MgO)</span>
                  <input
                    type="radio"
                    name="tipo"
                    value="dolomitica"
                    checked={tipo === 'dolomitica'}
                    onChange={(e) => setTipo(e.target.value)}
                    className="h-4 w-4 text-primary"
                    aria-label="Selecionar cal dolomítica"
                  />
                </label>
              </div>
            </div>

            {tipo === 'calcitica' ? (
              <div className="space-y-3 rounded-lg border border-border/50 bg-muted/20 p-4">
                <Label htmlFor="teorCaO" className="text-sm font-normal text-foreground">Teor de CaO (%)</Label>
                <Input
                  id="teorCaO"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={teorCaO}
                  onChange={(e) => setTeorCaO(Number(e.target.value))}
                  className="h-10 rounded-md border-border bg-background focus:border-primary"
                />
                <p className="text-xs text-muted-foreground/80 leading-relaxed">Utilize o teor oficial do laudo para comparar fornecedores.</p>
              </div>
            ) : (
              <div className="space-y-3 rounded-lg border border-border/50 bg-muted/20 p-4">
                <Label htmlFor="teorMgO" className="text-sm font-normal text-foreground">Teor de MgO (%)</Label>
                <Input
                  id="teorMgO"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={teorMgO}
                  onChange={(e) => setTeorMgO(Number(e.target.value))}
                  className="h-10 rounded-md border-border bg-background focus:border-primary"
                />
                <p className="text-xs text-muted-foreground/80 leading-relaxed">O MgO diferencia a cal dolomítica. Informe o teor para avaliar o custo real.</p>
              </div>
            )}

            <div className="grid gap-6 sm:grid-cols-2">
              <div className="space-y-3 rounded-lg border border-border/50 bg-muted/20 p-4">
                <Label htmlFor="precoProduto" className="text-sm font-normal text-foreground">Preço do produto (R$/t)</Label>
                <Input
                  id="precoProduto"
                  type="number"
                  step="0.01"
                  min="0"
                  value={precoProduto}
                  onChange={(e) => setPrecoProduto(Number(e.target.value))}
                  className="h-10 rounded-md border-border bg-background focus:border-primary"
                />
              </div>
              <div className="space-y-3 rounded-lg border border-border/50 bg-muted/20 p-4">
                <Label htmlFor="frete" className="text-sm font-normal text-foreground">Frete (R$/t)</Label>
                <Input
                  id="frete"
                  type="number"
                  step="0.01"
                  min="0"
                  value={frete}
                  onChange={(e) => setFrete(Number(e.target.value))}
                  className="h-10 rounded-md border-border bg-background focus:border-primary"
                />
              </div>
            </div>

            <div className="rounded-lg border border-border/50 bg-muted/20 p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <Label className="text-sm font-normal text-foreground">Incluir frete</Label>
                  <p className="text-xs text-muted-foreground/80">Altere conforme a negociação.</p>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={incluirFrete} onCheckedChange={setIncluirFrete} aria-label="Alternar inclusão de frete" />
                  <Badge variant="outline" className="rounded-full px-3 py-1 text-xs font-normal">
                    {incluirFrete ? 'Com frete' : 'Sem frete'}
                  </Badge>
                </div>
              </div>
            </div>

            <Button
              onClick={calcular}
              className="w-full rounded-lg bg-primary py-2.5 text-sm font-normal text-primary-foreground transition hover:bg-primary/90"
            >
              Calcular eficiência
            </Button>
          </div>

          <div className="space-y-8">
            <div className="space-y-2 text-center">
              <h3 className="text-lg font-normal text-foreground">Resultados da análise</h3>
              <p className="text-sm text-muted-foreground/80">Visão clara dos custos e pureza do produto informado.</p>
            </div>

            <div className="space-y-6">
              <div className="rounded-lg border border-border/50 bg-muted/20 p-5">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
                    <span>Preço total considerado</span>
                    <span className="font-normal text-foreground">
                      {results.precoTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
                    <span>Óxido alvo</span>
                    <span className="flex items-center gap-2 font-normal text-foreground">
                      {tipo === 'calcitica' ? 'CaO' : 'MgO'}
                      <Badge variant="secondary" className={`whitespace-nowrap ${qualidade.tone}`}>
                        {qualidade.label}
                      </Badge>
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Pureza informada</span>
                      <span>{Math.min(100, Math.max(0, teorAtual)).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%</span>
                    </div>
                    <div className="mt-2 h-1 w-full rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-primary"
                        style={{ width: `${Math.max(0, Math.min(100, teorAtual))}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Card className="rounded-lg border border-border/50 bg-muted/20 shadow-none">
                  <CardContent className="space-y-2 p-4">
                    <p className="text-xs text-muted-foreground">Custo por ponto</p>
                    <p className="text-xl font-normal text-foreground">
                      {results.custoPorPonto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      <span className="text-sm font-normal text-muted-foreground"> / ponto</span>
                    </p>
                    <p className="text-xs text-muted-foreground/80">Valor por ponto percentual do óxido escolhido.</p>
                  </CardContent>
                </Card>
                <Card className="rounded-lg border border-border/50 bg-muted/20 shadow-none">
                  <CardContent className="space-y-2 p-4">
                    <p className="text-xs text-muted-foreground">Custo por kg</p>
                    <p className="text-xl font-normal text-foreground">
                      {results.custoPorKgOxido.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      <span className="text-sm font-normal text-muted-foreground"> / kg</span>
                    </p>
                    <p className="text-xs text-muted-foreground/80">Investimento por kg de óxido efetivo recebido.</p>
                  </CardContent>
                </Card>
              </div>

              <Card className="rounded-lg border border-border/50 bg-muted/20 shadow-none">
                <CardContent className="space-y-3 p-4">
                  <h3 className="text-sm font-normal text-foreground">Interpretação rápida</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground/90">{results.interpretacao}</p>
                  <p className="text-xs text-muted-foreground/70">Confirme os dados com laudos atualizados e recomendações técnicas.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        <div className="mt-12 border-t border-border/30 pt-4 text-center text-xs text-muted-foreground/60">
          © Quimical — cálculo local, nenhum dado é enviado ou armazenado.
        </div>
      </CardContent>
    </Card>
  );
};

export default CalCalculator;